import { useDeleteCompetitionFileMutation, useUploadCompetitionFileMutation } from '../../files/mutations';
import { AddCompetitionAdminRequestRequest, Competition, CompetitionDetail } from '~/apis/ssf';
import { CompetitionLevelSelect } from '~/pages/level/components/competition-level-select';
import { withAuthentication, WithAuthenticationProps } from '~/hocs/withAuthentication';
import { InvalidCompetitionAdminDialog } from './invalid-competition-admin-dialog';
import { useSyncCompetitionAdminMutation } from '../../admin/mutations';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { useCompetitionFileListQuery } from '../../files/queries';
import { AddAdministratorForm } from './add-administrator-form';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { FileList } from '~/components/FileList/FileList';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { AxiosResponse, isAxiosError } from 'axios';
import { placeholder } from '~/utils/placeholder';
import { Badge } from '~/components/Badge/Badge';
import { Grid } from '~/components/Grid/Grid';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { Pill } from '~/components/Pill/Pill';
import { useNavigate } from 'react-router';
import { schema, Schema } from '../schema';
import { Input } from '~/components/Input';
import { ChangeEvent, useState } from 'react';
import { DistrictMultiSelect } from '~/pages/district/components/district-multi-select';

interface Props extends WithAuthenticationProps {
  onSubmit: (payload: Schema) => Promise<AxiosResponse<Competition, unknown>>;
  isPending?: boolean;
  defaultValues?: Pick<
    CompetitionDetail,
    | 'category'
    | 'contact_email'
    | 'contact_first_name'
    | 'contact_last_name'
    | 'contact_phone'
    | 'end_date'
    | 'files'
    | 'location'
    | 'name'
    | 'organizer'
    | 'payment_info'
    | 'reg_deadline'
    | 'start_date'
    | 'open_externally'
    | 'is_team_comp'
    | 'registrations_limit'
  > & {
    districts: number[];
    admins: string[];
  };
}

export const CompetitionForm = withAuthentication<Props>(({ defaultValues, user, onSubmit, isPending }) => {
  const [invalidEmail, setInvalidEmail] = useState<string>();

  const form = useForm<Schema>({
    resolver: zodResolver(schema) as unknown as Resolver<Schema>,
    defaultValues: {
      category: defaultValues?.category,
      contact_email: defaultValues?.contact_email ?? '',
      contact_first_name: defaultValues?.contact_first_name ?? '',
      contact_last_name: defaultValues?.contact_last_name ?? '',
      contact_phone: defaultValues?.contact_phone ?? '',
      end_date: defaultValues?.end_date ?? '',
      files: defaultValues?.files.map(file => file.id) ?? [],
      location: defaultValues?.location ?? '',
      districts: defaultValues?.districts ?? [],
      name: defaultValues?.name,
      open_externally: defaultValues?.open_externally ? true : false,
      is_team_comp: defaultValues?.is_team_comp ? true : false,
      registrations_limit: defaultValues?.registrations_limit ?? null,
      organizer: defaultValues?.organizer ?? '',
      payment_info: defaultValues?.payment_info ?? '',
      reg_deadline: defaultValues?.reg_deadline ? defaultValues.reg_deadline.split('T')[0] : '',
      start_date: defaultValues?.start_date ?? '',
      admins: defaultValues?.admins ?? [user.email],
    },
  });

  const { dialog } = useUnsavedChangesGuard(form.formState);

  const { data: files = [], isLoading: isLoadingFiles } = useCompetitionFileListQuery({
    ids: form.watch('files') ?? [],
  });

  const syncAdminMutation = useSyncCompetitionAdminMutation();
  const uploadMutation = useUploadCompetitionFileMutation();
  const deleteMutation = useDeleteCompetitionFileMutation();

  const navigate = useNavigate();

  async function trySubmit(payload: Schema, onSuccess?: (data: Competition) => void) {
    try {
      const { data } = await onSubmit(payload);

      await syncAdminMutation.mutateAsync({
        emails: payload.admins,
        id: data.id,
      });

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      /**
       * Assuming user tried to add non-existing user
       */
      if (isAxiosError(error) && error.status === 404 && error.config?.data) {
        const request = JSON.parse(error.config.data as string) as AddCompetitionAdminRequestRequest;
        setInvalidEmail(request.email);
      }

      throw error;
    }
  }

  async function handleChange(files: File[]) {
    for (const file of files) {
      const { data } = await uploadMutation.mutateAsync({
        name: file.name,
        file: file,
      });

      form.setValue('files', [...(form.getValues('files') ?? []), data.id], { shouldDirty: true });
    }
  }

  async function handleDelete(fileId: string) {
    form.setValue('files', [...form.getValues('files').filter(id => id !== fileId)], { shouldDirty: true });
    await deleteMutation.mutateAsync(fileId);
  }

  const isTeamComp = form.watch('is_team_comp');

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(data =>
          trySubmit(data, data =>
            navigate(data.is_team_comp ? `/competitions/${data.id}` : `/competitions/${data.id}/set-categories`)
          )
        )}
        autoComplete="off"
      >
        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="name" label="Tävlingsnamn">
              <Input.Field
                placeholder={placeholder.name}
                {...form.register('name')}
                formState={form.formState}
                type="text"
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="category" label="Tävlingsnivå">
              <CompetitionLevelSelect
                {...form.register('category')}
                defaultValue={form.watch('category')}
                placeholder="Välj tävlingsnivå"
                formState={form.formState}
              />
            </Input.Group>
          </Grid.Item>
        </Grid>

        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="start_date" label="Startdatum">
              <Input.Field type="date" {...form.register('start_date')} formState={form.formState} />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="end_date" label="Slutdatum">
              <Input.Field type="date" {...form.register('end_date')} formState={form.formState} />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="reg_deadline" label="Sista anmälningsdag">
              <Input.Field type="date" {...form.register('reg_deadline')} formState={form.formState} />
            </Input.Group>
          </Grid.Item>
        </Grid>

        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="districts" label="Distrikt">
              <DistrictMultiSelect
                {...form.register('districts')}
                value={form.watch('districts')}
                formState={form.formState}
                onChange={values => {
                  form.setValue('districts', values, { shouldDirty: true });
                  form.trigger('districts');
                }}
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="organizer" label="Arrangör">
              <Input.Field {...form.register('organizer')} formState={form.formState} type="text" />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="location" label="Plats">
              <Input.Field {...form.register('location')} formState={form.formState} type="text" />
            </Input.Group>
          </Grid.Item>

          {!isTeamComp && (
            <Grid.Item cols={{ sm: 12, lg: 4 }}>
              <Input.Group formState={form.formState} htmlFor="registrations_limit" label="Max antal anmälningar">
                <Input.Field
                  {...form.register('registrations_limit')}
                  formState={form.formState}
                  placeholder="Ingen gräns"
                  inputMode="numeric"
                  type="number"
                  min="0"
                />
              </Input.Group>
            </Grid.Item>
          )}
        </Grid>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--size-8)' }}>
          <Input.InlineGroup formState={form.formState} htmlFor="open_externally" label="Öppen för externa föreningar?">
            <Input.Checkbox {...form.register('open_externally')} formState={form.formState} />
          </Input.InlineGroup>
          <Input.InlineGroup formState={form.formState} htmlFor="is_team_comp" label="Lagtävling?">
            {defaultValues ? (
              <Tooltip title="Det går inte att ändra lagtävling efter att tävlingen har skapats." as="span">
                <Input.Checkbox {...form.register('is_team_comp')} formState={form.formState} disabled />
              </Tooltip>
            ) : (
              <Input.Checkbox
                {...form.register('is_team_comp', {
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      form.setValue('registrations_limit', null, { shouldDirty: true });
                    }
                  },
                })}
                formState={form.formState}
              />
            )}
          </Input.InlineGroup>
        </div>

        <PageLayout.SubHeading>Administratörer</PageLayout.SubHeading>

        <AddAdministratorForm
          onSubmit={({ email }) => form.setValue('admins', [...form.getValues('admins'), email], { shouldDirty: true })}
        />

        <div style={{ display: 'flex', gap: 'var(--size-2)' }}>
          {form.watch('admins').map(email => {
            if (email === user.email) {
              return <Badge variant="default" label={`${user.email} (du)`} key={email} />;
            }

            return (
              <Pill
                label={email}
                key={email}
                onRemove={() =>
                  form.setValue(
                    'admins',
                    form.getValues('admins').filter(a => a !== email),
                    { shouldDirty: true }
                  )
                }
              />
            );
          })}
        </div>

        <PageLayout.SubHeading>Kontaktperson</PageLayout.SubHeading>

        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, md: 6, lg: 3 }}>
            <Input.Group formState={form.formState} htmlFor="contact_first_name" label="Förnamn">
              <Input.Field
                type="text"
                {...form.register('contact_first_name')}
                formState={form.formState}
                id="contact_firstname"
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, md: 6, lg: 3 }}>
            <Input.Group formState={form.formState} htmlFor="contact_last_name" label="Efternamn">
              <Input.Field
                type="text"
                {...form.register('contact_last_name')}
                formState={form.formState}
                id="contact_lastname"
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, md: 6, lg: 3 }}>
            <Input.Group formState={form.formState} htmlFor="contact_email" label="E-post">
              <Input.Field
                placeholder={placeholder.email}
                {...form.register('contact_email')}
                formState={form.formState}
                id="contact_email"
                inputMode="email"
                type="text"
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, md: 6, lg: 3 }}>
            <Input.Group formState={form.formState} htmlFor="contact_phone" label="Telefon">
              <Input.Field
                placeholder={placeholder.phone}
                {...form.register('contact_phone')}
                formState={form.formState}
                id="contact_phone"
                inputMode="tel"
                type="text"
              />
            </Input.Group>
          </Grid.Item>
        </Grid>

        <PageLayout.SubHeading>Betalning</PageLayout.SubHeading>

        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={form.formState} htmlFor="payment_info" label="PG-/BG-nummer">
              <Input.Field
                {...form.register('payment_info')}
                formState={form.formState}
                id="bankgiro_plusgiro"
                type="text"
              />
            </Input.Group>
          </Grid.Item>
        </Grid>

        <PageLayout.SubHeading>Dokument</PageLayout.SubHeading>

        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12 }}>
            <Input.File
              onChange={handleChange}
              isUploading={uploadMutation.isPending || isLoadingFiles}
              accept=".pdf"
            />
            <FileList onDelete={handleDelete} files={files.map(({ id, name }) => ({ id, filename: name }))} />
          </Grid.Item>
        </Grid>

        <Button.Group align="right">
          {!isTeamComp && (
            <Button
              onClick={form.handleSubmit(data => trySubmit(data, data => navigate(`/competitions/${data.id}`)))}
              isLoading={isPending}
              variant="secondary"
              type="button"
            >
              Spara & återgå
            </Button>
          )}

          <Button type="submit" isLoading={isPending}>
            {isTeamComp ? 'Spara' : 'Spara & vidare till kval'}
          </Button>
        </Button.Group>
      </form>

      <InvalidCompetitionAdminDialog email={invalidEmail} onClose={() => setInvalidEmail(undefined)} />

      {dialog}
    </FormProvider>
  );
});
